// src/app/modules/commissionDefault/CommissionDefault.tsx

import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import Swal from 'sweetalert2'

import { CommissionDefault as CommissionDefaultModel } from './core/CommissionDefault.model'
import { validatePercentage } from './core/CommissionDefault.utils'
import { configDefaultCommission, updateDefaultCommissionConfig } from './ apis/requestsApi'


interface CommissionDefaultString {
  techPartner: string
  pctBaseTech: string       // e.g. "3,5"
  pctBaseCommercial: string // e.g. "3,5"
}

// Lista de socios predefinidos para mostrar cuando no haya datos
const DEFAULT_PARTNERS = ['Passport', 'Conekta', 'Enefevo']

export const CommissionDefault: React.FC = () => {
  const intl = useIntl()
  const [data, setData] = useState<CommissionDefaultString[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string,string>>({})

  // Formatea número a "3,5"
  const fmt = (n: number) => `${n}`.replace('.', ',')

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const commissions = await configDefaultCommission()
        let formatted: CommissionDefaultString[]

        if (commissions.length === 0) {
          // Sin datos: preparamos filas vacías
          formatted = DEFAULT_PARTNERS.map(tp => ({
            techPartner: tp,
            pctBaseTech: '',
            pctBaseCommercial: '',
          }))
        } else {
          // Con datos: formateamos
          formatted = commissions.map(c => ({
            techPartner: c.techPartner,
            pctBaseTech: fmt(c.pctBaseTech),
            pctBaseCommercial: fmt(c.pctBaseCommercial),
          }))
        }

        setData(formatted)
      } catch {
        Swal.fire('Error', 'No se pudieron cargar las comisiones', 'error')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const onChange = (idx: number, field: 'pctBaseTech' | 'pctBaseCommercial', raw: string) => {
    const updated = [...data]
    updated[idx]![field] = raw
    setData(updated)

    const numeric = parseFloat(raw.replace(',', '.'))
    const key = `${idx}-${field}`

    if (!validatePercentage(numeric)) {
      setErrors(e => ({ ...e, [key]: '0–100, hasta 2 decimales' }))
    } else {
      setErrors(e => {
        const { [key]: _, ...rest } = e
        return rest
      })
    }
  }

  const canSave = !loading && !saving && Object.keys(errors).length === 0

  const handleSave = async () => {
    setSaving(true)
    try {
      const payload: CommissionDefaultModel[] = data
        .map(item => ({
          techPartner: item.techPartner,
          pctBaseTech: parseFloat(item.pctBaseTech.replace(',', '.')),
          pctBaseCommercial: parseFloat(item.pctBaseCommercial.replace(',', '.')),
        }))
        .filter(c => !isNaN(c.pctBaseTech) && !isNaN(c.pctBaseCommercial))

      await updateDefaultCommissionConfig(payload)
      Swal.fire('¡Listo!', 'Comisiones guardadas correctamente', 'success')
    } catch {
      Swal.fire('Error', 'Falló al guardar las comisiones', 'error')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <p>Cargando comisiones…</p>
  }

  return (
    <div className="card p-4">
      <h3>
        {intl.formatMessage({
          id: 'COMMISSION_DEFAULT.TITLE',
          defaultMessage: 'Comisión por Defecto del Socio',
        })}
      </h3>
      <p className="text-muted mb-4">
        {intl.formatMessage({
          id: 'COMMISSION_DEFAULT.SUBTITLE',
          defaultMessage: 'Establece la comisión por defecto para cada socio',
        })}
      </p>

      <div className="table-responsive mb-4">
        <table className="table table-bordered align-middle">
          <thead>
            <tr>
              <th>Socio Tecnológico</th>
              <th>Comisión socio tecnológico  (%)</th>
              <th>Comisión socio comercial (%)</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, idx) => (
              <tr key={item.techPartner}>
                <td>{item.techPartner}</td>

                {/* Comisión Tecnológica */}
                <td>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="text"
                      className={`form-control ${errors[`${idx}-pctBaseTech`] ? 'is-invalid' : ''}`}
                      value={item.pctBaseTech}
                      onChange={e => onChange(idx, 'pctBaseTech', e.target.value)}
                    />
                    <span style={{ marginLeft: '0.5rem' }}>%</span>
                  </div>
                  {errors[`${idx}-pctBaseTech`] && (
                    <div className="invalid-feedback d-block">
                      {errors[`${idx}-pctBaseTech`]}
                    </div>
                  )}
                </td>

                {/* Comisión Comercial */}
                <td>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="text"
                      className={`form-control ${errors[`${idx}-pctBaseCommercial`] ? 'is-invalid' : ''}`}
                      value={item.pctBaseCommercial}
                      onChange={e => onChange(idx, 'pctBaseCommercial', e.target.value)}
                    />
                    <span style={{ marginLeft: '0.5rem' }}>%</span>
                  </div>
                  {errors[`${idx}-pctBaseCommercial`] && (
                    <div className="invalid-feedback d-block">
                      {errors[`${idx}-pctBaseCommercial`]}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-end">
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={!canSave}
        >
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </div>
  )
}
